<!-- A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. -->
<script>
  import Table from "$lib/components/Table/Table.svelte";
  import { getStateName } from "$utils/states.js";
  import { FORMATTERS } from "$utils/formatters.js";
  import { getConfig, getTableProps } from "$lib/utils/config.js";

  const { parsedData, currentState } = $props();

  const fmtInt = FORMATTERS.number;
  const fmtTenths = FORMATTERS.numberTenths;
  const config = getConfig();

  const stateName = $derived(currentState ? getStateName(currentState) : "");

  const mileage = $derived.by(() => {
    if (!parsedData || !currentState) return null;
    const row = parsedData.getRow(currentState, "2026-01-01");
    return {
      metro: row.key_mi_rail_metro,
      light_rail: row.key_mi_rail_light,
      commuter_rail: row.key_mi_rail_commuter,
      intercity: row.key_mi_intercity
    };
  });

  /**
   * @param {number | string | null | undefined} val
   * @param {string} label
   * @returns {string}
   */
  function mileagePhrase(val, label) {
    const num = Number(val);
    if (!val || isNaN(num) || num === 0) return `0 miles of ${label}`;
    return `${fmtInt(Math.round(num))} miles of ${label}`;
  }

  const mileageText = $derived.by(() => {
    if (!mileage) return "";
    const metro = mileagePhrase(mileage.metro, "metro rail");
    const light = mileagePhrase(mileage.light_rail, "light rail or streetcar service");
    const commuter = mileagePhrase(mileage.commuter_rail, "commuter rail");
    const intercity = mileagePhrase(mileage.intercity, "intercity rail");

    const mainParts = [metro, light, commuter].filter(Boolean);
    let text = `In addition to bus service, ${stateName} has ${mainParts[0]}`;
    if (mainParts.length === 2) {
      text += `, ${mainParts[1]}`;
    } else if (mainParts.length === 3) {
      text += `, ${mainParts[1]}, ${mainParts[2]}`;
    }
    text += ",";

    const intercityNum = Number(mileage.intercity);
    if (!isNaN(intercityNum) && intercityNum > 0) {
      text += ` and ${intercity}.`;
    } else {
      text += ` and 0 miles of intercity rail.`;
    }

    return text;
  });

  const tableData = $derived.by(() => {
    if (!parsedData || !currentState) return [];
    const allDetail = parsedData.getDetailDataForId(currentState);
    const datelessRows = allDetail.filter((r) => r.date === null);
    const rows2020 = allDetail.filter((r) => r.date === "2022-01-01");

    const urbanRows = rows2020
      .sort((a, b) => (Number(b.pop_20_24) || 0) - (Number(a.pop_20_24) || 0))
      .map((r) => {
        const dateless = datelessRows.find((dl) => dl.name_e === r.name_e);
        return {
          name: r.name_e,
          per_cap_transit_vmt: dateless?.key_per_cap_transit_vmt,
          per_cap_transit_rides: dateless?.key_per_cap_transit_rides
        };
      });

    // Prepend state averages row
    const stateRow = parsedData.getRow(currentState, null);
    const avgRow = {
      name: `Average of all urban areas in ${stateName}`,
      per_cap_transit_vmt: stateRow.key_per_cap_transit_vmt_urb_average,
      per_cap_transit_rides: stateRow.key_per_cap_transit_rides_urb_average
    };

    return [avgRow, ...urbanRows];
  });

  const tableCols = ["name", "per_cap_transit_vmt", "per_cap_transit_rides"];
  const tableHeaders = $derived.by(() => {
    const currentConfig = config?.();
    const { headers } = currentConfig
      ? getTableProps(currentConfig, [
          "key_per_cap_transit_vmt_urb_average",
          "key_per_cap_transit_rides_urb_average"
        ])
      : { headers: {} };

    return {
      name: "Urban area",
      per_cap_transit_vmt: headers.key_per_cap_transit_vmt_urb_average ?? "Per capita transit VMT",
      per_cap_transit_rides:
        headers.key_per_cap_transit_rides_urb_average ?? "Per capita transit rides"
    };
  });
  const tableFormatters = {
    per_cap_transit_vmt: fmtTenths,
    per_cap_transit_rides: fmtTenths
  };

  // Scatterplot data: urban areas only (no state average), with population
  const scatterData = $derived.by(() => {
    if (!parsedData || !currentState) return [];
    const allDetail = parsedData.getDetailDataForId(currentState);
    const datelessRows = allDetail.filter((r) => r.date === null);
    const rows2020 = allDetail.filter((r) => r.date === "2022-01-01");

    return rows2020
      .map((r) => {
        const dateless = datelessRows.find((dl) => dl.name_e === r.name_e);
        const vmt = dateless?.key_per_cap_transit_vmt;
        const rides = dateless?.key_per_cap_transit_rides;
        const pop = Number(r.pop_20_24) || 0;
        if (vmt == null || rides == null || typeof vmt !== "number" || typeof rides !== "number")
          return null;
        return { name: r.name_e, vmt, rides, pop };
      })
      .filter((d) => d != null);
  });
</script>

{#if currentState}
  <h5 class="section-title">What kind of transit service is available in {stateName}?</h5>
  <p class="section-description">{mileageText}</p>
  <Table
    data={tableData}
    cols={tableCols}
    headers={tableHeaders}
    formatters={tableFormatters}
    labelColumn={true}
    collapsible={true}
    scrollable={true}
  />
{:else}
  <p class="placeholder-text">Select a state to see transit service data.</p>
{/if}

<style>
  .section-title {
    margin-block-start: 0;
  }
  .placeholder-text {
    color: var(--color-gray-shade-dark);
  }
  .scatter-title {
    margin-block-start: var(--spacing-8);
    margin-block-end: 0;
  }
  .scatter-description {
    margin-bottom: var(--spacing-4);
    color: var(--color-gray-shade-dark);
    font-size: var(--font-size-small);
  }
  .scatter-container {
    width: 100%;
    aspect-ratio: 6 / 3;
    position: relative;
  }
</style>
